/**
 * MemoriaDoMembro — aba "Memória" do painel do membro no Squad Designer (SPEC A6 FR-13).
 *
 * Só leitura: o que este agente já aprendeu neste projeto.
 *   - lições ativas do projeto (versão, ocorrências, regra de origem);
 *   - "Mostrar lições encerradas" relê com `historico=1` (as versões substituídas);
 *   - lições de outros projetos do tenant, rotuladas "de outro projeto — referência";
 *   - episódios de execuções anteriores;
 *   - o que ficou fora do limite (`omitidos`) — nunca corta em silêncio.
 */
import { useState } from 'react';
import { C } from '@/theme/palette';
import { useMemoriaDoAgente } from '@/api/agents';
import { mensagemDoErro } from './comum';

function dataPtBr(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('pt-BR');
}

function Secao({ titulo, children, testId }) {
  return (
    <div data-testid={testId} style={{ marginTop: 14 }}>
      <div style={{ fontSize: 11, color: C.neutralMuted, letterSpacing: '0.04em', fontWeight: 600, marginBottom: 6 }}>
        {titulo}
      </div>
      {children}
    </div>
  );
}

const itemStyle = {
  fontSize: 12, color: C.neutral, lineHeight: 1.45, padding: '8px 10px', marginBottom: 6,
  backgroundColor: C.blackElev, border: `1px solid ${C.blackBorder}`, borderRadius: 6,
};

const metaStyle = { fontSize: 10.5, color: C.neutralMuted, marginTop: 4 };

function Vazio({ children }) {
  return <div style={{ fontSize: 11.5, color: C.neutralMuted }}>{children}</div>;
}

function Omitidos({ n, rotulo }) {
  if (!n) return null;
  return (
    <div style={{ fontSize: 11, color: C.warn, marginTop: 2 }}>
      +{n.toLocaleString('pt-BR')} {rotulo} fora do limite (não entram no prompt).
    </div>
  );
}

/**
 * @param {{ agent: string, projectId: string|null|undefined }} props
 */
export function MemoriaDoMembro({ agent, projectId }) {
  const [historico, setHistorico] = useState(false);
  const q = useMemoriaDoAgente(agent, projectId, { historico });
  const dados = q.data || {};
  const licoes = dados.licoes || [];
  const outros = dados.licoes_outros_projetos || [];
  const episodios = dados.episodios || [];
  const omitidos = dados.omitidos || {};

  return (
    <div role="region" aria-label={`Memória de ${agent}`} data-testid="memoria-do-membro">
      <div style={{ fontSize: 12.5, color: C.neutralDim, lineHeight: 1.45 }}>
        O que este agente já aprendeu neste projeto.
      </div>
      <div style={{ fontSize: 11, color: C.neutralMuted, marginTop: 4 }}>
        Só leitura — entra no prompt do agente como “O que você já aprendeu neste projeto”.
      </div>

      {!projectId && <Vazio>Salve o projeto para ver a memória do agente.</Vazio>}
      {q.isLoading && <div style={{ fontSize: 11.5, color: C.neutralMuted, marginTop: 10 }}>Carregando memória…</div>}
      {q.isError && (
        <div role="alert" style={{ fontSize: 11.5, color: C.bad, marginTop: 10 }}>
          {mensagemDoErro(q.error, 'Não foi possível ler a memória do agente.')}
        </div>
      )}

      {q.isSuccess && (
        <>
          <Secao titulo={`LIÇÕES DESTE PROJETO (${licoes.length})`} testId="memoria-licoes">
            {licoes.length === 0 && <Vazio>Nenhuma lição ainda — elas nascem dos problemas resolvidos nas execuções.</Vazio>}
            {licoes.map((l) => (
              <div key={l.id} style={{ ...itemStyle, opacity: l.ativa ? 1 : 0.6 }} data-testid={`licao-${l.id}`}>
                <div>{l.conteudo}</div>
                <div style={metaStyle}>
                  v{l.version} · {l.ocorrencias.toLocaleString('pt-BR')} ocorrência{l.ocorrencias === 1 ? '' : 's'}
                  {l.regra ? <> · regra <code>{l.regra}</code></> : null}
                  {!l.ativa ? ' · encerrada (substituída por versão mais nova)' : ''}
                  {l.criada_em ? ` · ${dataPtBr(l.criada_em)}` : ''}
                </div>
              </div>
            ))}
            <Omitidos n={omitidos.licoes} rotulo="lição(ões)" />
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: C.neutralDim, marginTop: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={historico} onChange={(e) => setHistorico(e.target.checked)} />
              Mostrar lições encerradas
            </label>
          </Secao>

          <Secao titulo={`EXECUÇÕES ANTERIORES (${episodios.length})`} testId="memoria-episodios">
            {episodios.length === 0 && <Vazio>Nenhum episódio deste agente em execuções anteriores.</Vazio>}
            {episodios.map((e) => (
              <div key={e.id} style={itemStyle} data-testid={`episodio-${e.id}`}>
                <div>{e.conteudo}</div>
                <div style={metaStyle}>
                  {e.tipo}{e.resultado ? ` · ${e.resultado}` : ''}{e.em ? ` · ${dataPtBr(e.em)}` : ''}
                </div>
              </div>
            ))}
            <Omitidos n={omitidos.episodios} rotulo="episódio(s)" />
          </Secao>

          <Secao titulo={`DE OUTROS PROJETOS (${outros.length})`} testId="memoria-outros-projetos">
            {outros.length === 0 && <Vazio>Nenhuma lição deste agente em outros projetos.</Vazio>}
            {outros.map((l) => (
              <div key={l.id} style={itemStyle} data-testid={`licao-outro-${l.id}`}>
                <div style={{ fontSize: 10.5, color: C.warn, marginBottom: 4 }}>de outro projeto — referência</div>
                <div>{l.conteudo}</div>
              </div>
            ))}
            <Omitidos n={omitidos.licoes_outros_projetos} rotulo="lição(ões) de outros projetos" />
          </Secao>
        </>
      )}
    </div>
  );
}
